import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  X, 
  FileText, 
  MessageSquare, 
  Mail, 
  Mic,
  Upload,
  Check,
  AlertCircle,
  Loader2,
  ChevronRight,
  Edit3
} from 'lucide-react'
import { clsx } from 'clsx'
import { useCreateEvidence, useUploadEvidence } from '@/hooks/useDeals'
import { useToast } from '@/components'
import { parseApiError, formatErrorMessage } from '@/services/api'
import { evidenceService } from '@/services/dealService'
import type { EvidenceSourceType, EvidenceAnalysisResult, ExtractedPerson, ExtractedFact } from '@/types'

interface EvidenceIngestionModalProps {
  isOpen: boolean
  onClose: () => void
  dealId: string
  dealName: string
}

// 证据来源类型配置
const SOURCE_TYPES: {
  type: EvidenceSourceType
  label: string
  icon: React.ElementType
  description: string
  placeholder: string
}[] = [
  {
    type: 'meeting_note',
    label: '会议纪要',
    icon: FileText,
    description: '粘贴会议纪要或拜访记录',
    placeholder: '例如：今天与张总进行了首次沟通，他表示对我们的方案很感兴趣，但需要IT部门李经理进行技术评估...'
  },
  {
    type: 'im',
    label: 'IM消息',
    icon: MessageSquare,
    description: '粘贴微信/钉钉聊天记录',
    placeholder: '例如：\n客户：这个功能能实现吗？\n我：可以的，我们有成熟的方案\n客户：好，我跟领导汇报一下'
  },
  {
    type: 'email',
    label: '邮件',
    icon: Mail,
    description: '粘贴邮件正文',
    placeholder: '例如：\n收件人：xxx@company.com\n主题：关于XX项目合作\n正文：...'
  },
  {
    type: 'audio',
    label: '录音/转写',
    icon: Mic,
    description: '粘贴录音转写文本（或上传文件）',
    placeholder: '粘贴已转写的录音文本...'
  },
]

export default function EvidenceIngestionModal({
  isOpen,
  onClose,
  dealId,
  dealName,
}: EvidenceIngestionModalProps) {
  // 状态
  const [step, setStep] = useState<'input' | 'preview' | 'confirm'>('input')
  const [selectedType, setSelectedType] = useState<EvidenceSourceType>('meeting_note')
  const [content, setContent] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [analysisResult, setAnalysisResult] = useState<EvidenceAnalysisResult | null>(null)
  const [editedPersons, setEditedPersons] = useState<ExtractedPerson[]>([])
  const [editedFacts, setEditedFacts] = useState<ExtractedFact[]>([])
  const [followUpAnswer, setFollowUpAnswer] = useState('')  // 智能追问回复
  const [evidenceId, setEvidenceId] = useState<string | null>(null)  // 新增：保存证据ID
  const [isEditing, setIsEditing] = useState(false)  // 新增：编辑模式

  // Mutations
  const createEvidence = useCreateEvidence()
  const uploadEvidence = useUploadEvidence()
  const toast = useToast()
  
  // 确认中状态（假设诊断可能需要 10-20 秒）
  const [isConfirming, setIsConfirming] = useState(false)

  const isLoading = createEvidence.isPending || uploadEvidence.isPending || isConfirming

  // 重置状态
  const resetState = useCallback(() => {
    setStep('input')
    setSelectedType('meeting_note')
    setContent('')
    setFile(null)
    setAnalysisResult(null)
    setEditedPersons([])
    setEditedFacts([])
    setEvidenceId(null)
    setIsEditing(false)
    setFollowUpAnswer('')
    setIsConfirming(false)
  }, [])

  // 关闭弹窗
  const handleClose = useCallback(() => {
    if (!isLoading) {
      resetState()
      onClose()
    }
  }, [isLoading, resetState, onClose])

  // 提交证据
  const handleSubmit = useCallback(async () => {
    if (!content.trim() && !file) return

    try {
      let result
      
      if (file) {
        // 文件上传
        result = await uploadEvidence.mutateAsync({
          dealId,
          sourceType: selectedType,
          file,
        })
      } else {
        // 文本输入
        result = await createEvidence.mutateAsync({
          dealId,
          sourceType: selectedType,
          contentRaw: content,
          occurredAt: new Date().toISOString(),
        })
      }

      // 设置分析结果并进入预览（API 响应已通过拦截器转换为 camelCase）
      if (result.analysisResult) {
        setEvidenceId(result.id)  // 保存证据ID
        setAnalysisResult(result.analysisResult)
        setEditedPersons(result.analysisResult.persons || [])
        setEditedFacts(result.analysisResult.facts || [])
        setStep('preview')
        toast.success('证据分析完成', '请确认抽取结果')
      } else {
        // 没有分析结果，直接确认
        setEvidenceId(result.id)  // 保存证据ID
        setStep('confirm')
        toast.success('证据已录入')
      }
    } catch (error) {
      console.error('Failed to submit evidence:', error)
      const apiError = parseApiError(error)
      const { title, message } = formatErrorMessage(apiError)
      toast.error(title, message)
    }
  }, [content, file, dealId, selectedType, createEvidence, uploadEvidence, toast])

  // 确认抽取结果（无编辑）
  const handleConfirm = useCallback(async () => {
    if (!evidenceId) {
      toast.error('错误', '证据ID缺失')
      return
    }

    try {
      // 立即显示加载状态和提示
      setIsConfirming(true)
      toast.info('正在分析假设树...', '这可能需要 10-20 秒，请稍候')
      
      await evidenceService.confirmEvidence(evidenceId)
      
      setIsConfirming(false)
      toast.success('证据已确认', '假设树已自动更新')
      setStep('confirm')
    
      // 2秒后关闭
      setTimeout(() => {
        handleClose()
      }, 2000)
    } catch (error) {
      setIsConfirming(false)
      console.error('Failed to confirm evidence:', error)
      const apiError = parseApiError(error)
      const { title, message } = formatErrorMessage(apiError)
      toast.error(title, message)
    }
  }, [evidenceId, handleClose, toast])

  // 保存编辑结果
  const handleSaveEdits = useCallback(async () => {
    if (!evidenceId) {
      toast.error('错误', '证据ID缺失')
      return
    }

    try {
      await evidenceService.updateEvidenceTags(evidenceId, {
        extractedTags: {
          persons: editedPersons.map(p => ({
            name: p.name,
            role: p.role,
            attitude: p.attitude,
            title: p.title,
            department: p.department
          })),
          facts: editedFacts.map(f => ({
            category: f.category,
            content: f.content
          }))
        }
      })
      setIsEditing(false)
      toast.success('修改已保存', '证据状态已更新为"已纠正"')
    } catch (error) {
      console.error('Failed to save edits:', error)
      const apiError = parseApiError(error)
      const { title, message } = formatErrorMessage(apiError)
      toast.error(title, message)
    }
  }, [evidenceId, editedPersons, editedFacts, toast])

  // 文件选择
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setFile(selectedFile)
      setContent(`[已选择文件: ${selectedFile.name}]`)
    }
  }, [])

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* 背景遮罩 */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={handleClose}
        />

        {/* 弹窗内容 */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* 头部 */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">添加证据</h2>
              <p className="text-sm text-gray-500">{dealName}</p>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* 步骤指示器 */}
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center gap-2">
              {['input', 'preview', 'confirm'].map((s, i) => (
                <div key={s} className="flex items-center">
                  <div className={clsx(
                    'w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-colors',
                    step === s ? 'bg-primary-600 text-white' :
                    ['preview', 'confirm'].indexOf(step) > ['preview', 'confirm'].indexOf(s) ? 'bg-green-500 text-white' :
                    'bg-gray-200 text-gray-500'
                  )}>
                    {['preview', 'confirm'].indexOf(step) > i ? <Check className="w-3 h-3" /> : i + 1}
                  </div>
                  {i < 2 && (
                    <ChevronRight className="w-4 h-4 text-gray-300 mx-1" />
                  )}
                </div>
              ))}
              <span className="ml-2 text-sm text-gray-500">
                {step === 'input' ? '录入证据' : step === 'preview' ? '确认抽取' : '完成'}
              </span>
            </div>
          </div>

          {/* 内容区域 */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {/* Step 1: 输入 */}
            {step === 'input' && (
              <div className="space-y-6">
                {/* 来源类型选择 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    证据来源类型
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {SOURCE_TYPES.map((source) => {
                      const Icon = source.icon
                      return (
                        <button
                          key={source.type}
                          onClick={() => setSelectedType(source.type)}
                          className={clsx(
                            'flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left',
                            selectedType === source.type
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-300'
                          )}
                        >
                          <div className={clsx(
                            'p-2 rounded-lg',
                            selectedType === source.type ? 'bg-primary-100' : 'bg-gray-100'
                          )}>
                            <Icon className={clsx(
                              'w-5 h-5',
                              selectedType === source.type ? 'text-primary-600' : 'text-gray-500'
                            )} />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{source.label}</div>
                            <div className="text-xs text-gray-500">{source.description}</div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* 内容输入 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    证据内容
                  </label>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder={SOURCE_TYPES.find(s => s.type === selectedType)?.placeholder}
                    rows={8}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-400">
                      {content.length} 字符
                    </span>
                    <label className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
                      <Upload className="w-4 h-4" />
                      上传文件
                      <input
                        type="file"
                        accept=".txt,.doc,.docx,.pdf"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                {/* 提示 */}
                <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p>
                    系统将自动抽取关键人物、业务事实、信号强弱等信息，您可以在下一步进行确认和纠正。
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: 预览抽取结果 */}
            {step === 'preview' && analysisResult && (
              <div className="space-y-6">
                {/* 摘要 */}
                <div className="p-4 bg-gray-50 rounded-xl">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">内容摘要</h3>
                  <p className="text-gray-600">{analysisResult.summary || '暂无摘要'}</p>
                </div>

                {/* 抽取的人物 */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-700">识别到的关键人</h3>
                    {!isEditing && (
                      <button 
                        onClick={() => setIsEditing(true)}
                        className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                      >
                      <Edit3 className="w-3 h-3" />
                      编辑
                    </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {editedPersons.length > 0 ? editedPersons.map((person, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg">
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-medium text-sm">
                          {person.name.charAt(0)}
                        </div>
                        {isEditing ? (
                          <div className="flex-1 flex items-center gap-2">
                            <input
                              type="text"
                              value={person.name}
                              onChange={(e) => {
                                const newPersons = [...editedPersons]
                                newPersons[index] = { ...person, name: e.target.value }
                                setEditedPersons(newPersons)
                              }}
                              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                              placeholder="姓名"
                            />
                            <select
                              value={person.role || ''}
                              onChange={(e) => {
                                const newPersons = [...editedPersons]
                                newPersons[index] = { ...person, role: e.target.value }
                                setEditedPersons(newPersons)
                              }}
                              className="px-2 py-1 text-xs border border-gray-300 rounded"
                            >
                              <option value="">角色</option>
                              <option value="EB">EB</option>
                              <option value="TB">TB</option>
                              <option value="UB">UB</option>
                              <option value="Coach">Coach</option>
                              <option value="Other">Other</option>
                            </select>
                            <button
                              onClick={() => {
                                const newPersons = editedPersons.filter((_, i) => i !== index)
                                setEditedPersons(newPersons)
                              }}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{person.name}</div>
                          <div className="text-xs text-gray-500">
                            {person.role && <span className="mr-2">角色: {person.role}</span>}
                            {person.attitude && <span>态度: {person.attitude}</span>}
                          </div>
                          {(person.title || person.department) && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              {[person.title, person.department].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </div>
                        )}
                      </div>
                    )) : (
                      <p className="text-sm text-gray-400">未识别到关键人</p>
                    )}
                  </div>
                </div>

                {/* 抽取的事实 */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-700">识别到的业务事实</h3>
                    {!isEditing && (
                      <button 
                        onClick={() => setIsEditing(true)}
                        className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                      >
                      <Edit3 className="w-3 h-3" />
                      编辑
                    </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {editedFacts.length > 0 ? editedFacts.map((fact, index) => (
                      <div key={index} className="p-3 bg-white border border-gray-200 rounded-lg">
                        {isEditing ? (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <select
                                value={fact.category}
                                onChange={(e) => {
                                  const newFacts = [...editedFacts]
                                  newFacts[index] = { ...fact, category: e.target.value }
                                  setEditedFacts(newFacts)
                                }}
                                className="px-2 py-1 text-xs border border-gray-300 rounded"
                              >
                                <option value="Budget">Budget</option>
                                <option value="Timeline">Timeline</option>
                                <option value="Risk">Risk</option>
                                <option value="Requirement">Requirement</option>
                                <option value="Other">Other</option>
                              </select>
                              <button
                                onClick={() => {
                                  const newFacts = editedFacts.filter((_, i) => i !== index)
                                  setEditedFacts(newFacts)
                                }}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <textarea
                              value={fact.content}
                              onChange={(e) => {
                                const newFacts = [...editedFacts]
                                newFacts[index] = { ...fact, content: e.target.value }
                                setEditedFacts(newFacts)
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              rows={2}
                              placeholder="事实内容"
                            />
                          </div>
                        ) : (
                          <>
                        <span className={clsx(
                          'inline-block px-2 py-0.5 rounded text-xs font-medium mb-1',
                          fact.category === 'Budget' ? 'bg-green-100 text-green-700' :
                          fact.category === 'Timeline' ? 'bg-blue-100 text-blue-700' :
                          fact.category === 'Risk' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        )}>
                          {fact.category}
                        </span>
                        <p className="text-sm text-gray-700">{fact.content}</p>
                          </>
                        )}
                      </div>
                    )) : (
                      <p className="text-sm text-gray-400">未识别到业务事实</p>
                    )}
                  </div>
                </div>

                {/* 信号强度 */}
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div>
                    <span className="text-sm text-gray-500">信号强度</span>
                    <div className={clsx(
                      'text-lg font-semibold',
                      ['Strong', 'strong'].includes(analysisResult.signalStrength || '') ? 'text-green-600' :
                      ['Medium', 'medium'].includes(analysisResult.signalStrength || '') ? 'text-amber-600' :
                      ['Weak', 'weak'].includes(analysisResult.signalStrength || '') ? 'text-gray-500' :
                      'text-gray-400'
                    )}>
                      {['Strong', 'strong'].includes(analysisResult.signalStrength || '') ? '强信号' :
                       ['Medium', 'medium'].includes(analysisResult.signalStrength || '') ? '中信号' :
                       ['Weak', 'weak'].includes(analysisResult.signalStrength || '') ? '弱信号' : '无信号'}
                    </div>
                  </div>
                  <div className="h-10 w-px bg-gray-200" />
                  <div>
                    <span className="text-sm text-gray-500">需求层级</span>
                    <div className="text-lg font-semibold text-gray-900">
                      {['Solution', 'solution'].includes(analysisResult.demandLayer || '') ? '方案层' :
                       ['Organization', 'organization'].includes(analysisResult.demandLayer || '') ? '组织层' : '个人层'}
                    </div>
                  </div>
                </div>

                {/* 智能追问交互 */}
                {analysisResult.followUpQuestion && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <h3 className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                      💡 建议补充
                      {analysisResult.missingInfoType && (
                        <span className="text-xs px-2 py-0.5 bg-amber-200 rounded-full">
                          {analysisResult.missingInfoType}
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-amber-700 mb-3">{analysisResult.followUpQuestion}</p>
                    
                    {/* 追问回复输入区 */}
                    <div className="space-y-2">
                      <textarea
                        value={followUpAnswer}
                        onChange={(e) => setFollowUpAnswer(e.target.value)}
                        placeholder="输入补充信息...（可选）"
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-amber-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setFollowUpAnswer('')}
                          className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700"
                        >
                          跳过
                        </button>
                        {followUpAnswer.trim() && (
                          <span className="text-xs text-amber-600">
                            ✓ 补充信息将与证据一起保存
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: 确认完成 */}
            {step === 'confirm' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">证据已录入</h3>
                <p className="text-gray-500">系统正在更新假设树和诊断结果...</p>
              </div>
            )}
          </div>

          {/* 底部操作栏 */}
          {step !== 'confirm' && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={step === 'input' ? handleClose : isEditing ? () => setIsEditing(false) : () => setStep('input')}
                disabled={isLoading}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                {step === 'input' ? '取消' : isEditing ? '取消编辑' : '返回修改'}
              </button>
              <div className="flex items-center gap-2">
                {step === 'preview' && isEditing && (
                  <button
                    onClick={handleSaveEdits}
                    disabled={isLoading}
                    className={clsx(
                      'px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors',
                      isLoading
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-amber-600 text-white hover:bg-amber-700'
                    )}
                  >
                    {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    保存修改
                  </button>
                )}
                {step === 'preview' && !isEditing && (
                  <button
                    onClick={handleConfirm}
                    disabled={isLoading}
                    className={clsx(
                      'px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors',
                      isLoading
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    )}
                  >
                    {isConfirming && <Loader2 className="w-4 h-4 animate-spin" />}
                    {isConfirming ? '正在分析假设树...' : '确认无误'}
                  </button>
                )}
                {step === 'input' && (
              <button
                    onClick={handleSubmit}
                disabled={isLoading || (!content.trim() && !file)}
                className={clsx(
                  'px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors',
                  isLoading || (!content.trim() && !file)
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                )}
              >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    提取信息
              </button>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

